import { UserData } from '../hooks/useUser';
import { db, auth as firebaseAuth } from '../firebase';
import { doc, updateDoc, increment, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Capacitor } from '@capacitor/core';

// Product IDs
export const PRODUCTS = {
  ARCADE_PACK: 'arcade_pack_15', // Consumable ($15 arcade pack)
  PRO_SUBSCRIPTION: 'pro_tier_monthly', // Subscription ($5/month)
  MILESTONE_LVL_5: 'milestone_lvl_5',
  MILESTONE_LVL_10: 'milestone_lvl_10',
  MILESTONE_LVL_15: 'milestone_lvl_15',
  MILESTONE_LVL_20: 'milestone_lvl_20',
  MILESTONE_LVL_25: 'milestone_lvl_25',
  MILESTONE_LVL_30: 'milestone_lvl_30',
  MILESTONE_LVL_40: 'milestone_lvl_40',
  MILESTONE_LVL_50: 'milestone_lvl_50',
  // Gem Packages
  GEMS_POUCH: 'gems_pouch',
  GEMS_BAG: 'gems_bag',
  GEMS_BOX: 'gems_box',
  GEMS_VAULT: 'gems_vault',
  GEMS_CHEST: 'gems_chest',
  GEMS_HOARD: 'gems_hoard',
  GEMS_GALACTIC: 'gems_galactic',
};

class PurchaseManager {
  private store: any = null;
  private isInitialized = false;

  constructor() {
    // Check if we are running in a native environment (Capacitor/Cordova)
    if (typeof window !== 'undefined' && (window as any).CdvPurchase) {
      this.store = (window as any).CdvPurchase.store;
    }
  }

  async initialize(userId: string) {
    if (!this.store || this.isInitialized) return;

    console.log('Initializing PurchaseManager...');

    // Register Products
    const productsToRegister = [
      { id: PRODUCTS.ARCADE_PACK, type: (window as any).CdvPurchase.ProductType.CONSUMABLE },
      { id: PRODUCTS.PRO_SUBSCRIPTION, type: (window as any).CdvPurchase.ProductType.PAID_SUBSCRIPTION },
      { id: PRODUCTS.MILESTONE_LVL_5, type: (window as any).CdvPurchase.ProductType.CONSUMABLE },
      { id: PRODUCTS.MILESTONE_LVL_10, type: (window as any).CdvPurchase.ProductType.CONSUMABLE },
      { id: PRODUCTS.MILESTONE_LVL_15, type: (window as any).CdvPurchase.ProductType.CONSUMABLE },
      { id: PRODUCTS.MILESTONE_LVL_20, type: (window as any).CdvPurchase.ProductType.CONSUMABLE },
      { id: PRODUCTS.MILESTONE_LVL_25, type: (window as any).CdvPurchase.ProductType.CONSUMABLE },
      { id: PRODUCTS.MILESTONE_LVL_30, type: (window as any).CdvPurchase.ProductType.CONSUMABLE },
      { id: PRODUCTS.MILESTONE_LVL_40, type: (window as any).CdvPurchase.ProductType.CONSUMABLE },
      { id: PRODUCTS.MILESTONE_LVL_50, type: (window as any).CdvPurchase.ProductType.CONSUMABLE },
      { id: PRODUCTS.GEMS_POUCH, type: (window as any).CdvPurchase.ProductType.CONSUMABLE },
      { id: PRODUCTS.GEMS_BAG, type: (window as any).CdvPurchase.ProductType.CONSUMABLE },
      { id: PRODUCTS.GEMS_BOX, type: (window as any).CdvPurchase.ProductType.CONSUMABLE },
      { id: PRODUCTS.GEMS_VAULT, type: (window as any).CdvPurchase.ProductType.CONSUMABLE },
      { id: PRODUCTS.GEMS_CHEST, type: (window as any).CdvPurchase.ProductType.CONSUMABLE },
      { id: PRODUCTS.GEMS_HOARD, type: (window as any).CdvPurchase.ProductType.CONSUMABLE },
      { id: PRODUCTS.GEMS_GALACTIC, type: (window as any).CdvPurchase.ProductType.CONSUMABLE }
    ].map(p => ({
      ...p,
      platform: this.isAndroid() ? (window as any).CdvPurchase.Platform.GOOGLE_PLAY : (window as any).CdvPurchase.Platform.APPLE_APPSTORE
    }));

    this.store.register(productsToRegister);

    // Handle Purchases
    this.store.when()
      .approved(async (transaction: any) => {
        console.log('Purchase approved:', transaction.productID);
        
        try {
          // Verify with Firebase Cloud Function
          let success = await this.verifyPurchaseWithBackend(userId, transaction);
          
          if (!success) {
            console.log('Backend verification failed, applying client-side fallback for testing...');
            try {
              const updates: any = {};
              if (transaction.productID === PRODUCTS.PRO_SUBSCRIPTION) {
                updates.isPremium = true;
                updates.is_pro_member = true;
                updates.subscription_expiry = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
              } else if (transaction.productID === PRODUCTS.ARCADE_PACK) {
                updates.user_credits = increment(1000);
                updates.gems = increment(100);
              } else if (transaction.productID.startsWith('gems_')) {
                const gemRewardsMap: Record<string, number> = {
                  gems_pouch: 200,
                  gems_bag: 900,
                  gems_box: 1900,
                  gems_vault: 3750,
                  gems_chest: 5200,
                  gems_hoard: 10000,
                  gems_galactic: 22000,
                };
                const amount = gemRewardsMap[transaction.productID];
                if (amount) {
                  updates.gems = increment(amount);
                }
              }
              
              if (Object.keys(updates).length > 0) {
                await updateDoc(doc(db, 'users', userId), updates);
                success = true;
              }
            } catch (fallbackError) {
              console.error('Client-side fallback failed:', fallbackError);
            }
          }
          
          if (success) {
            transaction.finish();
            console.log('Transaction finished successfully');
          } else {
            console.error('Purchase verification failed');
          }
        } catch (error) {
          console.error('Error during verification:', error);
        }
      });

    this.store.when()
      .verified((receipt: any) => {
        console.log('Receipt verified:', receipt);
        receipt.finish();
      });

    this.store.when()
      .finished((transaction: any) => {
        console.log('Transaction finished:', transaction.productID);
      });

    // Error handling
    this.store.error((error: any) => {
      console.error('Store Error:', error);
    });

    // Initialize the store
    await this.store.initialize([
        (window as any).CdvPurchase.Platform.GOOGLE_PLAY
    ]);
    
    this.isInitialized = true;
    console.log('PurchaseManager initialized');
  }

  async buyArcadePack() {
    if (!this.store) {
      console.warn('PurchaseManager not initialized or not on native platform');
      // Fallback or alert
      return;
    }
    const product = this.store.get(PRODUCTS.ARCADE_PACK);
    if (product) {
      product.getOffer().order();
    }
  }

  async buyGems(productId: string) {
    if (!this.store) {
      console.warn('PurchaseManager not initialized or not on native platform');
      return;
    }
    const product = this.store.get(productId);
    if (product) {
      product.getOffer().order();
    }
  }

  async subscribePro() {
    if (!this.store) {
      console.warn('PurchaseManager not initialized or not on native platform');
      return;
    }
    const product = this.store.get(PRODUCTS.PRO_SUBSCRIPTION);
    if (product) {
      product.getOffer().order();
    }
  }

  async buyMilestoneOffer(productId: string) {
    if (!this.store) {
      console.warn('PurchaseManager not initialized or not on native platform');
      return;
    }
    const product = this.store.get(productId);
    if (product) {
      product.getOffer().order();
    }
  }

  async restorePurchases() {
    if (!this.store) return;
    console.log('Restoring purchases...');
    await this.store.restore();
  }

  private async verifyPurchaseWithBackend(userId: string, transaction: any): Promise<boolean> {
    try {
      const functions = getFunctions();
      const verifyFunction = httpsCallable(functions, 'verifyGooglePlayPurchase');
      
      const response: any = await verifyFunction({
        userId,
        productId: transaction.productID,
        purchaseToken: transaction.purchaseToken,
        type: transaction.type === (window as any).CdvPurchase.ProductType.PAID_SUBSCRIPTION ? 'subscription' : 'consumable'
      });

      return response.data.success;
    } catch (error) {
      console.error('Backend verification error:', error);
      return false;
    }
  }

  // Helper to check if we are on Android
  isAndroid() {
    return Capacitor.getPlatform() === 'android';
  }
}

export const purchaseManager = new PurchaseManager();
